



































































































































































































































































































































































































import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  toRefs,
  useContext,
  watch,
} from "@nuxtjs/composition-api"
import debounce from "lodash/debounce"

import { getAreasOfInterest } from "@/api/query/areasOfInterestQuery"
import { useCustomerProfessionalsQuery } from "@/api/query/myProfessionalsQuery"
import {
  FilterProfessionalsParams,
  ProfessionalResponse,
  useProfessionalsQuery,
} from "@/api/query/professionalsQuery"
import { useProfessionalSpecialityTranslationQuery } from "@/api/query/specialitesQuery"
import MyProfessionalCard from "@/components/MyProfessionalCard.vue"
import ProfessionalCard from "@/components/ProfessionalCard.vue"
import useBottomNav from "@/composables/useBottomNav"
import useGeolocation from "@/composables/useGeolocation"
import useLanguage from "@/composables/useLanguage"
import useProfessionalLanguage from "@/composables/useProfessionalLanguage"
import { useQueryParams } from "@/composables/useQueryParams"
import { UserStatus } from "@/enums"
import { QueryParams } from "@/enums/QueryParams"
import { AreaOfInterest } from "@/types/models/AreaOfInterest"
import { User } from "@/types/models/User"

interface OptionArea extends AreaOfInterest {
  selected: boolean
}

export default defineComponent({
  components: { ProfessionalCard, MyProfessionalCard },
  props: {
    isGuest: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { toggleBottomNav } = useBottomNav()
    const { app } = useContext()
    const avatarUser = computed(() => app.$accessor.user.avatar)
    const currentUser = computed(() => app.$accessor.user.currentUser)
    const showFiltersModal = ref(false)
    const enabledFilters = ref(true)
    const specialitiesVisible = ref(false)
    const languagesVisible = ref(false)
    const showMyProfessionals = ref(true)
    const professionals = ref<User[]>([])
    const searchInput = ref<HTMLInputElement>()
    const locationInput = ref<HTMLInputElement>()
    const listProfessionals = ref<HTMLDivElement | undefined>(undefined)
    const {
      coordinates,
      coordinatesSync,
      getCurrentGeolocation,
      searchTermWorkplaces,
      locationAutocompleteFilters,
      isGeolocationAllowed,
    } = useGeolocation()

    const { append, remove, query } = useQueryParams()
    const areasOfInterest = ref<OptionArea[]>([])

    const page = ref(0)

    const readParamQueryValue = (queryParam: QueryParams, isArray: boolean) => {
      const paramQueryValue = query.value[queryParam]
      return typeof paramQueryValue === "string"
        ? JSON.parse(paramQueryValue)
        : isArray
        ? []
        : ""
    }

    const filters = reactive<FilterProfessionalsParams>({
      areasOfInterest: readParamQueryValue(QueryParams.AREAS_OF_INTEREST, true),
      searchKeywords: readParamQueryValue(QueryParams.SEARCH_KEY_WORDS, true),
      specialityIds: readParamQueryValue(QueryParams.SPECIALITY_IDS, true),
      languages: readParamQueryValue(QueryParams.LANGUAGES, true),
      location: readParamQueryValue(QueryParams.LOCATION, false),
      longitude: readParamQueryValue(QueryParams.LONGITUDE, false),
      latitude: readParamQueryValue(QueryParams.LATITUDE, false),
    })

    const searchValue = ref<string>(
      readParamQueryValue(QueryParams.SEARCH_KEY_WORDS, true).join(" "),
    )

    locationAutocompleteFilters.searchTerm = readParamQueryValue(
      QueryParams.LOCATION,
      false,
    )

    const clearFilters = () => {
      searchValue.value = ""
      filters.searchKeywords = []
      filters.location = ""
      languageSearchTerm.value = ""
      aoiSearchTerm.value = ""
      specialitySearchTerm.value = ""
      locationAutocompleteFilters.searchTerm = ""
      clearSpecialitiesFilter()
      clearAreaOfInterestFilter()
      clearLanguageFilter()
      collapseFilters()
    }

    const collapseFilters = () => {
      specialitiesVisible.value = false
      languagesVisible.value = false
    }

    watch(showFiltersModal, (newVal) => {
      if (newVal) {
        collapseFilters()
        toggleBottomNav(false, true)
      } else {
        toggleBottomNav(true, true)
      }
    })

    watch(coordinates, (newVal) => {
      if (newVal.longitude && newVal.latitude) {
        filters.longitude = newVal.longitude.toString()
        filters.latitude = newVal.latitude.toString()
      }
    })

    watch(filters, () => {
      if (page.value !== 0) {
        page.value = 0
      }
    })

    const clearSpecialitiesFilter = () => {
      filters.specialityIds = []
    }

    const clearAreaOfInterestFilter = () => {
      areasOfInterest.value.forEach((area) => {
        area.selected = false
      })
    }

    const clearLanguageFilter = () => {
      filters.languages = []
    }

    const specialitySearchTerm = ref("")
    const languageSearchTerm = ref("")

    const { data: specialities, isLoading: isLoadingSpecialities } =
      useProfessionalSpecialityTranslationQuery(
        reactive({ localeId: computed(() => app.i18n.locale) }),
      )

    const { getLangFullName } = useLanguage()

    const { allProfessionalLanguageNames } = useProfessionalLanguage()

    const getProfessionalLanguages = (professional: User) => {
      return getLangFullName(professional.professionalData?.languageIds!).join(
        ", ",
      )
    }

    const filteredLanguages = computed(() => {
      const searchTerm = languageSearchTerm.value.toLowerCase()
      return allProfessionalLanguageNames.value.filter(
        (language) =>
          language.id.toLowerCase().includes(searchTerm) ||
          language.name.toString().toLowerCase().includes(searchTerm),
      )
    })

    const filteredSpecialities = computed(() => {
      const searchTerm = specialitySearchTerm.value.toLowerCase()
      return specialities.value?.filter((speciality) => {
        return speciality.name.toString().toLowerCase().includes(searchTerm)
      })
    })

    const maxPages = ref(0)

    const {
      isLoading: isLoadingProfessionals,
      isFetching: isFetchingProfessionals,
    } = useProfessionalsQuery(
      reactive({
        ...toRefs(filters),
        page,
      }),
      reactive({
        enabled: enabledFilters && coordinatesSync,
        onSuccess: (data: ProfessionalResponse) => {
          enabledFilters.value = false
          if (page.value > 0) {
            professionals.value.push(...data.professionals)
          } else {
            professionals.value = data.professionals
          }
          maxPages.value = data.maxPageNumber - 1 // starts with 0
          updateQueryParams()
        },
      }),
    )

    const updateQueryParams = () => {
      Object.values(QueryParams).forEach((queryParam) => {
        const queryParamValue = query.value[queryParam]
        if (typeof queryParamValue === "string" && queryParamValue.length) {
          remove(queryParam)
        }

        const filterValue = filters[queryParam]

        if (!filterValue?.length) return
        append({ [queryParam]: JSON.stringify(filterValue) })
      })
    }

    const isCustomer = computed(() => Boolean(currentUser.value?.customerData))

    const { data: myProfessionals, isLoading: isLoadingMyProfessionals } =
      useCustomerProfessionalsQuery({
        // TODO remove 'false' after my professionals is back
        enabled: false && !props.isGuest && isCustomer,
      })

    const { isLoading: aoiLoading } = getAreasOfInterest(
      {
        professional_status: UserStatus.PROFESSIONAL_VERIFIED,
      },
      {
        onSuccess(data) {
          areasOfInterest.value = data.map((area) => {
            return {
              ...area,
              selected: filters.areasOfInterest?.includes(area.id) ?? false,
            }
          })
        },
      },
    )

    const aoiSearchTerm = ref("")

    const filteredAreasOfInterest = computed(() => {
      const searchTerm = aoiSearchTerm.value.toLowerCase()
      return areasOfInterest.value.filter(
        (area) =>
          area.name.toLowerCase().includes(searchTerm) ||
          area.id.toString().includes(searchTerm),
      )
    })

    watch(
      () => areasOfInterest.value,
      () => {
        filters.areasOfInterest = areasOfInterest.value
          .filter((area) => area.selected)
          .map((area) => area.id)
      },
      { deep: true },
    )

    const getProfessionalSpecialities = (professional: User) => {
      const professionalData = professional.professionalData
      if (!professionalData || !specialities.value) return ""

      const professionalSpecialitiesIds = professionalData.specialityIds
      return specialities.value
        .filter((speciality) =>
          professionalSpecialitiesIds.includes(speciality.specialityId),
        )
        .filter((speciality) => speciality)
        .map((speciality) => speciality.name)
        .join(" | ")
    }

    const numbersFilters = computed(() => {
      let numbersFiltersFilled = 0
      const areasOfInterestSelected = areasOfInterest.value.filter(
        (area) => area.selected,
      ).length
      if (filters.searchKeywords?.length) numbersFiltersFilled++
      if (filters.languages?.length) numbersFiltersFilled++
      if (filters.specialityIds?.length) numbersFiltersFilled++
      if (locationAutocompleteFilters.searchTerm?.length) numbersFiltersFilled++
      if (areasOfInterestSelected) numbersFiltersFilled++

      return numbersFiltersFilled
    })

    const focusSearch = () => {
      searchInput.value?.focus()
    }

    const prepareSearchKeywords = debounce((e: Event) => {
      const input = e.target as HTMLInputElement
      const newValue = input.value.trim()
      const searchIsEmptyOrMoreThanThreeWords =
        !newValue.length || newValue.length >= 3
      if (searchIsEmptyOrMoreThanThreeWords) {
        const spaceCaracter = " "

        filters.searchKeywords = newValue.length
          ? newValue.split(spaceCaracter)
          : []
      }
      enabledFilters.value = true
    }, 600)

    const { resultsWorkplaces } = searchTermWorkplaces()
    const limitedAutocompletedResults = computed(() =>
      resultsWorkplaces.value ? resultsWorkplaces.value?.slice(0, 5) : [],
    )

    const updateLocation = () => {
      const geolocation = resultsWorkplaces.value?.find(
        (result) =>
          result.description === locationAutocompleteFilters.searchTerm,
      )
      if (geolocation) {
        filters.location = geolocation.description
      } else if (!locationAutocompleteFilters.searchTerm) {
        filters.location = ""
      }
    }

    const fetchWhenBottomReached = () => {
      if (isFetchingProfessionals.value || isLoadingProfessionals.value) return
      if (page.value >= maxPages.value) return
      if (
        listProfessionals.value?.scrollHeight! -
          listProfessionals.value?.scrollTop! <=
        listProfessionals.value?.clientHeight! + 20
      ) {
        page.value = page.value + 1
      }
    }

    const updateLocationWorkplaces = () => {
      getCurrentGeolocation()
      locationInput.value?.focus()
    }

    onMounted(() => {
      document.querySelector("#__layout")?.classList.add("full")
      document.querySelector("#__layout")?.classList.add("tw-overflow-hidden")
      listProfessionals.value?.addEventListener(
        "scroll",
        fetchWhenBottomReached,
        false,
      )
    })

    onUnmounted(() => {
      document
        .querySelector("#__layout")
        ?.classList.remove("tw-overflow-hidden")
      document.querySelector("#__layout")?.classList.remove("full")
    })

    return {
      professionals,
      myProfessionals,
      filters,
      clearFilters,
      avatarUser,
      currentUser,
      enabledFilters,
      isFetchingProfessionals,
      isLoadingSpecialities,
      isLoadingMyProfessionals,
      specialitiesVisible,
      languagesVisible,
      getProfessionalSpecialities,
      clearSpecialitiesFilter,
      clearAreaOfInterestFilter,
      clearLanguageFilter,
      specialitySearchTerm,
      languageSearchTerm,
      filteredSpecialities,
      filteredLanguages,
      getProfessionalLanguages,
      aoiLoading,
      areasOfInterest,
      aoiSearchTerm,
      filteredAreasOfInterest,
      showFiltersModal,
      showMyProfessionals,
      numbersFilters,
      toggleBottomNav,
      prepareSearchKeywords,
      searchInput,
      searchValue,
      focusSearch,
      getCurrentGeolocation,
      locationAutocompleteFilters,
      limitedAutocompletedResults,
      updateLocation,
      listProfessionals,
      isGeolocationAllowed,
      updateLocationWorkplaces,
      locationInput,
      coordinatesSync,
    }
  },
})
